/**
 * Financial Metrics TanStack Query Hooks
 */

import { useQuery } from '@tanstack/react-query';
import {
  fetchFinancialSummary,
  fetchPaymentAging,
  fetchRevenueByType,
  fetchRevenueByPeriod,
  fetchTopAccounts,
  fetchTopProjects,
  fetchQuoteFunnel,
  fetchQuotesPipelineSummary,
  fetchQuotesPipeline,
} from '../api';
import type { QuotesPipelineQueryParams } from '../types';

// Query key factory
export const financialKeys = {
  all: ['operations-financial'] as const,
  summary: () => [...financialKeys.all, 'summary'] as const,
  paymentAging: () => [...financialKeys.all, 'payment-aging'] as const,
  revenueByType: () => [...financialKeys.all, 'revenue-by-type'] as const,
  revenueByPeriod: (months: number) => [...financialKeys.all, 'revenue-by-period', months] as const,
  topAccounts: (limit: number) => [...financialKeys.all, 'top-accounts', limit] as const,
  topProjects: (limit: number, status?: string) => [...financialKeys.all, 'top-projects', limit, status] as const,
  quoteFunnel: (months: number) => [...financialKeys.all, 'quote-funnel', months] as const,
  quotesPipelineSummary: (params?: { estimated_delivery_from?: string; estimated_delivery_to?: string }) =>
    [...financialKeys.all, 'quotes-pipeline-summary', params] as const,
  quotesPipeline: (params?: QuotesPipelineQueryParams) =>
    [...financialKeys.all, 'quotes-pipeline', params] as const,
};

/**
 * Hook to fetch financial summary
 */
export function useFinancialSummary() {
  return useQuery({
    queryKey: financialKeys.summary(),
    queryFn: fetchFinancialSummary,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch payment aging breakdown
 */
export function usePaymentAging() {
  return useQuery({
    queryKey: financialKeys.paymentAging(),
    queryFn: fetchPaymentAging,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to fetch revenue by project type
 */
export function useRevenueByType() {
  return useQuery({
    queryKey: financialKeys.revenueByType(),
    queryFn: fetchRevenueByType,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to fetch monthly revenue data
 */
export function useRevenueByPeriod(months: number = 12) {
  return useQuery({
    queryKey: financialKeys.revenueByPeriod(months),
    queryFn: () => fetchRevenueByPeriod(months),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to fetch top accounts by revenue
 */
export function useTopAccounts(limit: number = 10) {
  return useQuery({
    queryKey: financialKeys.topAccounts(limit),
    queryFn: () => fetchTopAccounts(limit),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to fetch top projects by revenue
 */
export function useTopProjects(limit: number = 10, status?: string) {
  return useQuery({
    queryKey: financialKeys.topProjects(limit, status),
    queryFn: () => fetchTopProjects(limit, status),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to fetch quote conversion funnel
 */
export function useQuoteFunnel(months: number = 6) {
  return useQuery({
    queryKey: financialKeys.quoteFunnel(months),
    queryFn: () => fetchQuoteFunnel(months),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to fetch quotes pipeline summary
 */
export function useQuotesPipelineSummary(params?: {
  estimated_delivery_from?: string;
  estimated_delivery_to?: string;
  created_date_from?: string;
  created_date_to?: string;
  invoiced_date_from?: string;
  invoiced_date_to?: string;
}) {
  return useQuery({
    queryKey: financialKeys.quotesPipelineSummary(params),
    queryFn: () => fetchQuotesPipelineSummary(params),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to fetch quotes pipeline list
 */
export function useQuotesPipeline(params?: QuotesPipelineQueryParams) {
  return useQuery({
    queryKey: financialKeys.quotesPipeline(params),
    queryFn: () => fetchQuotesPipeline(params),
    staleTime: 1000 * 60 * 5,
  });
}
